import Box from '@mui/material/Box';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import RadioGroup, { type RadioGroupProps } from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { type Key, type ReactNode, type Ref, useId } from 'react';
import { Radio } from '../Radio/Radio.tsx';
import type { RadioOptionType } from './types.ts';

export type RadioFieldProps<TValue = string> = RadioGroupProps & {
  'data-test'?: string;
  fieldRef?: Ref<HTMLDivElement>;
  fullWidth?: FormControlProps['fullWidth'];
  getOptionKey?: (value: TValue) => Key;
  helperText?: string;
  isDisabled?: boolean;
  isError?: boolean;
  isReadOnly?: boolean;
  label?: ReactNode;
  margin?: FormControlProps['margin'];
  options: RadioOptionType<TValue>[];
  variant?: 'chip' | 'standard';
};

export const RadioField = <TValue = string>({
  'data-test': dataTest,
  fieldRef,
  fullWidth,
  getOptionKey,
  helperText,
  isDisabled,
  isError,
  isReadOnly,
  label,
  margin,
  options,
  variant = 'standard',
  ...rest
}: RadioFieldProps<TValue>) => {
  const id = useId();

  return (
    <FormControl data-test={dataTest} error={isError} fullWidth={fullWidth} margin={margin}>
      {label && (
        <FormLabel id={id} error={isError}>
          {label}
        </FormLabel>
      )}
      <RadioGroup aria-labelledby={id} {...rest} ref={fieldRef}>
        {options.map((option, index) => (
          <Box
            key={option.key ?? getOptionKey?.(option.value) ?? `radio-option-${index}`}
            sx={rest.row ? { ':not(:last-of-type)': { mr: 1.5 } } : undefined}
          >
            <FormControlLabel
              aria-label={option.ariaLabel}
              className={`MuiFormGroup-${variant}`}
              checked={option.value === rest.value}
              control={
                <Radio
                  readOnly={isReadOnly}
                  // @ts-expect-error there is no type definition for data-* attributes for inputProps, although it works
                  slotProps={{ input: { 'data-test': option.dataTest || option['data-test'] } }}
                />
              }
              data-test={`${option.dataTest || option['data-test']}-label`}
              disabled={option.isDisabled || isDisabled}
              label={option.label}
              sx={{ mr: option.tooltip ? 0.5 : undefined, ...option.labelSx }}
              value={option.value}
            />

            {option.tooltip && (
              <Tooltip placement={option.tooltipPlacement} title={option.tooltip}>
                <IconButton>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
      </RadioGroup>
      {helperText && <FormHelperText sx={{ ml: 0 }}>{helperText}</FormHelperText>}
    </FormControl>
  );
};
